/**
 * @fileoverview Public consent revocation page.
 *
 * Opened from the standing "manage consent" link in summary emails, or from
 * the under-13 email-plus confirmation's cancel link (?source=confirmation).
 *
 * The GET only renders this page; the actual revocation happens on the
 * confirm button's POST — so email scanners that prefetch links can't
 * revoke consent by accident.
 */

import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldOff, ShieldX, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { getErrorMessage } from "@/lib/api";

const PAGE_BG = {
  background:
    "radial-gradient(ellipse at 50% 0%, rgba(244,207,8,0.055) 0%, rgba(244,207,8,0.015) 40%, transparent 70%) no-repeat, #09090b",
};

const ConsentRevokePage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const fromConfirmation = searchParams.get("source") === "confirmation";

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const res = await axios.get(`${API}/consent/revoke/${token}`, { signal: controller.signal });
        setInfo(res.data);
        if (res.data.status === "revoked") setDone(true);
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err.response?.data?.detail || "This link is not valid.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [token]);

  const handleRevoke = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/consent/revoke/${token}`, {
        reason: fromConfirmation ? "email_plus_cancel" : "parent_revoke",
      });
      setDone(true);
    } catch (err) {
      toast.error(getErrorMessage(err, "Something went wrong — please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={PAGE_BG}
        data-theme="dark"
      >
        <Loader2 className="w-8 h-8 text-[#f4cf08] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-center p-8"
        style={PAGE_BG}
        data-theme="dark"
      >
        <div>
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldX className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-light text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Link Not Valid
          </h1>
          <p className="text-zinc-400 text-sm max-w-sm">{error}</p>
          <p className="text-zinc-500 text-xs mt-4">
            Use the link from the most recent summary email, or contact the instructor.
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-center p-8"
        style={PAGE_BG}
        data-theme="dark"
      >
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="w-16 h-16 bg-[#f4cf08]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-[#f4cf08]" />
          </div>
          <h1 className="text-xl font-light text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Consent withdrawn
          </h1>
          <p className="text-zinc-400 text-sm max-w-md mx-auto">
            Recording is now locked for {info?.student_first_name || "this student"}'s lessons, and
            any remaining audio and transcripts have been deleted. The instructor has been
            notified.
          </p>
          <p className="text-zinc-500 text-xs mt-4 max-w-md mx-auto">
            Lesson summaries that were already emailed are kept with the teacher's records. To have
            those deleted too, email{" "}
            <a href={`mailto:${info?.support_email}`} className="text-zinc-400 hover:underline">
              {info?.support_email}
            </a>
            .
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={PAGE_BG}
      data-theme="dark"
    >
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img src="/logo192.png" alt="Hans" className="h-10 rounded-lg" />
            <span
              className="text-lg font-semibold text-white tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Hans
            </span>
          </div>
          <div className="w-14 h-14 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldOff className="w-7 h-7 text-red-400" />
          </div>
          <h1 className="text-2xl font-light text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            {fromConfirmation ? "Cancel the consent you gave?" : "Withdraw recording consent?"}
          </h1>
          <p className="text-zinc-400 text-sm">
            For <span className="text-white font-medium">{info.student_first_name}</span>'s lessons
            with <span className="text-white font-medium">{info.teacher_name}</span>.
          </p>
        </div>

        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 mb-5">
          <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">
            {info.explanation}
          </p>
        </div>

        <Button
          onClick={handleRevoke}
          disabled={submitting}
          className="w-full bg-red-500/90 hover:bg-red-500 text-white font-medium rounded-full py-3 transition-colors mb-3"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldOff className="w-4 h-4 mr-2" />}
          {fromConfirmation ? "Cancel consent" : "Withdraw consent"}
        </Button>
        <p className="text-center text-zinc-600 text-xs">
          Changed your mind? Just close this page — nothing happens until you confirm.
        </p>
      </motion.div>
    </div>
  );
};

export default ConsentRevokePage;
